import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {ContentPanel, OpacityIcon} from '@taopix/taopix-design-system';
import {AssetType, BoxLayer, BoxType, BoxTypeShape, NumBool} from '@taopix/taopix-core';
import {useTaopix} from '../../../Context/TaopixContext';
import classNames from 'classnames';
import {useExperience} from '../../../Experience/ExperienceProvider';
import {RangeWithValue} from '@taopix/taopix-design-system';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import ToolPaletteSection from '../../../ToolPalette/ToolPaletteSection';
import PaletteInformationMessage from '../PaletteInformationMessage';

export type TransparencyProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

const Transparency = ({sectionKey, item}: TransparencyProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation();

  const {experience} = useExperience();
  const {backgroundOptions, pictureBoxOptions, shapeBoxOptions, stickerOptions} = experience.EDITOR;

  const [notAvailableMessage, setNotAvailableMessage] = useState<string>();
  const [showSection, setShowSection] = useState<boolean>(false);
  const [disableSection, setDisableSection] = useState<boolean>(false);

  const [shapeTransparency, setShapeTransparency] = useState<number>(item?.alpha as number);

  // update the state when item.alpha changes
  useEffect(() => {
    if (item?.alpha) {
      setShapeTransparency(item?.alpha);
    }
  }, [item?.alpha]);

  const isActiveInExperience = (item: ComputedPageItem) => {
    const boxType = item.type;
    // Return whether or not this option is enabled in the experience for the selected item
    if (boxType === BoxType.Image && item.layer === BoxLayer.Background) {
      return backgroundOptions.transparency;
    }
    if (
      boxType === BoxType.Image &&
      item?.asset?.assetType !== AssetType.Background &&
      item?.asset?.assetType !== AssetType.ScrapbookImage &&
      item?.hirespath !== ''
    ) {
      return pictureBoxOptions.transparency;
    }
    if (
      boxType === BoxType.Shape &&
      [BoxTypeShape.Line, BoxTypeShape.Oval, BoxTypeShape.Polygon, BoxTypeShape.Rect].includes(
        item?.shape
      )
    ) {
      return shapeBoxOptions.transparency;
    }
    if (AssetType.ScrapbookImage === item?.hiresfiletype) {
      return stickerOptions.transparency;
    } else {
      return false;
    }
  };

  useEffect(() => {
    // If there is a selected item
    if (item && item.ref !== -1) {
      // Show or hide this section based on the relevant experience and advanced editing configuration
      setShowSection(isActiveInExperience(item));
      // Enable or disable this section based on the item's protection
      setDisableSection(
        NumBool.True === item?.protectcontents ||
          (item.errors?.imageMissing as boolean) ||
          ui.state.swapActive
      );
      // Set the message to 'not available' in case the section was hidden or disabled
      setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
    } else {
      // If no item is selected, hide the section button
      setShowSection(false);
      // And show the 'please select an item' message
      setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
    }
  }, [
    item?.asset,
    item?.asset?.assetType,
    item?.type,
    item?.image,
    item?.shape,
    ui.state.swapActive,
  ]);

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'lg:flex-1',
    'space-y-sm',
    'sm:pb-md',
    'sm:h-mobilePaletteContentHeight',
    'sm:max-w-full',
    'overflow-y-auto'
  );

  if (
    ui.state.palette.section !== 'box-settings-palette' &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}`
  ) {
    return null;
  }

  return (
    <ToolPaletteSection
      title={t('boxpalettes:str_TitleTransparency')}
      buttonIcon={<OpacityIcon />}
      sectionKey={sectionKey}
      buttonClasses={sectionButtonClasses}
      useFocusMode
      subSection={true}
      showButton={showSection}
      disabled={disableSection}
      id="transparencyPalette"
      buttonLayout="horizontal"
      parentselected={true}
    >
      <div className={wrapperClasses}>
        {!showSection || disableSection ? (
          // If the palette button has been hidden or disabled (by the experience, box protection or nothing is selected),
          // show the relevant 'unavailable' message in the palette in case the palette was left open when its status changed
          <PaletteInformationMessage message={notAvailableMessage} />
        ) : (
          // Only show the sub options if the palette is allowed in the experience and the content is not protected
          <>
            <ContentPanel>
              <RangeWithValue
                data-testid={'box-settings-transparency-slider'}
                key={`shape-transparency-${item?.ref}`}
                label={t('boxpalettes:str_TitleTransparency')}
                value={shapeTransparency}
                id={'shape-transparency-slider'}
                min={10}
                max={100}
                submitOnBlur={true}
                onValueChange={(value: number, interacting?: boolean) => {
                  if (item?.ref && item?.ref !== -1) {
                    app.core.dispatch('SHAPE.UPDATE_OPACITY', value, interacting).catch(error => {
                      console.error(error);
                    });
                  }
                }}
                isSmallScreen={ui.state.isSmallScreen}
              />
            </ContentPanel>
          </>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default Transparency;
